import React, { useState } from 'react';
import {
    Alert,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Btn, InputController, ResourcesList, setProgress } from 'Components';
import Yup from 'Utils/Yup';
import { mapValidationErrors } from 'Utils/Http/MapValidationErrors';
import { CERTIFICATE_CATEGORIES, NO_CERTIFICATE_CATEGORY } from 'Constants/CertificateNames';
import { verifyCertificateName } from 'Services/CertificateNamesSvc';
import VerificationStatusChip from './VerificationStatusChip';
import ThresholdSettingCard from './ThresholdSettingCard';
import { formatCertificateCategory, formatSimilarity } from './CertificateHelpers';

const VERIFY_NAME_BTN = 'VERIFY_NAME_BTN';

const schema = Yup.object({
    name: Yup.string().trim().required(),
});

// Which note to show under the result. Each status means something different
// and 'ambiguous' in particular must not read like a pass.
const STATUS_SEVERITY = {
    verified: 'success',
    ambiguous: 'warning',
    not_verified: 'error',
};

const useStyles = makeStyles((theme) => ({
    h4Text: {
        fontSize: '18px',
        color: 'rgb(3, 166, 90)',
        fontWeight: '500',
    },
}));

/**
 * One labelled result field. Laid out so the four of them read across as
 * "Entered Name: ... | Best Match: ... | Similarity: 97% | Status: Verified".
 */
const ResultField = ({ label, children }) => (
    <Grid item xs={12} sm={6} md={3}>
        <h6 className="labelForm pb-1 text-gray-500">{label}</h6>
        <div className="text-base font-medium break-words">{children}</div>
    </Grid>
);

/**
 * The operator tool: run a name through the same check the public certificate
 * page runs, without issuing a certificate or a download token.
 *
 * The similarity, the threshold and the pass/fail decision are all the
 * backend's - nothing here recomputes them.
 */
const VerifyNamePage = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const spacing = 4;

    const [result, setResult] = useState(null);
    // Not a form field: it scopes the search the way the public page can, and
    // there is nothing to validate about it.
    const [category, setCategory] = useState(NO_CERTIFICATE_CATEGORY);

    const form = useForm({
        resolver: yupResolver(schema),
        defaultValues: { name: '' },
    });
    const { control, handleSubmit, setError } = form;

    const handleFormSubmit = async (values) => {
        setProgress(VERIFY_NAME_BTN, true);
        try {
            const verification = await verifyCertificateName({ name: values.name, category });
            setResult(verification);
        } catch (error) {
            // A stale result next to a failed request reads as if the failure
            // were the answer.
            setResult(null);

            const fieldErrors = error?.response?.data?.errors;

            if (error?.response?.status === 422 && fieldErrors) {
                mapValidationErrors(fieldErrors).forEach(({ name, message, type }) => {
                    setError(name, { type, message });
                });
            } else {
                toast.error(error?.response?.data?.message);
            }
        }
        setProgress(VERIFY_NAME_BTN, false);
    };

    const severity = result ? STATUS_SEVERITY[result.status] ?? 'info' : 'info';

    return (
        <ResourcesList
            headerProps={{ title: t('admin.certificateNames.verify.title') }}
            Section={
                <div className="mt-8">
                    <ThresholdSettingCard
                        // A verdict reached under the old threshold, left on
                        // screen beside the new one, reads as if the new one
                        // had produced it.
                        onSaved={() => setResult(null)}
                    />

                    <p className="text-xs text-gray-500 pb-4">
                        {t('admin.certificateNames.verify.intro')}
                    </p>

                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                        <Grid container spacing={spacing} alignItems="flex-end">
                            <Grid item xs={12} md={6}>
                                <InputController
                                    control={control}
                                    name="name"
                                    label={t('admin.certificateNames.verify.nameLabel')}
                                    placeholder={t('admin.certificateNames.verify.namePlaceholder')}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <h6 className="labelForm pb-2">
                                    {t('admin.certificateNames.verify.categoryLabel')}
                                </h6>
                                <TextField
                                    select
                                    fullWidth
                                    id="verify-name-category"
                                    value={category}
                                    onChange={(event) => setCategory(event.target.value)}
                                    // MUI renders a blank box for an empty
                                    // value unless told to show the option.
                                    SelectProps={{ displayEmpty: true }}
                                >
                                    <MenuItem value={NO_CERTIFICATE_CATEGORY}>
                                        {t('admin.certificateNames.allCategories')}
                                    </MenuItem>
                                    {CERTIFICATE_CATEGORIES.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {t(`admin.certificateNames.categories.${value}`)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Btn fullWidth={false} type="submit" name={VERIFY_NAME_BTN}>
                                    {t('admin.certificateNames.verify.submit')}
                                </Btn>
                            </Grid>
                        </Grid>
                    </form>

                    {result ? (
                        <Paper variant="outlined" className="mt-8 p-4">
                            <Grid container spacing={2}>
                                <ResultField label={t('admin.certificateNames.verify.enteredName')}>
                                    {result.entered_name}
                                </ResultField>
                                <ResultField label={t('admin.certificateNames.verify.bestMatch')}>
                                    {result.matched_name ?? t('admin.certificateNames.verify.noMatch')}
                                </ResultField>
                                <ResultField label={t('admin.certificateNames.verify.similarity')}>
                                    {formatSimilarity(result.similarity)}
                                </ResultField>
                                <ResultField label={t('admin.certificateNames.verify.status')}>
                                    <VerificationStatusChip status={result.status} size="medium" />
                                </ResultField>
                            </Grid>

                            <Alert severity={severity} className="mt-4">
                                {t(`admin.certificateNames.verify.${result.status}Hint`)}
                            </Alert>

                            <p className="text-xs text-gray-500 pt-3">
                                {t('admin.certificateNames.verify.threshold', {
                                    threshold: result.threshold,
                                })}
                            </p>
                            <p className="text-xs text-gray-500">
                                {t('admin.certificateNames.verify.thresholdHint')}
                            </p>
                            <p className="text-xs text-gray-500">
                                {t('admin.certificateNames.verify.loggedAs', {
                                    id: result.verification_id,
                                })}
                            </p>

                            {/* Only an ambiguous result needs the candidate list:
                                it is there to show WHY the check could not be
                                decided, so the operator can see the competing
                                approved names side by side. */}
                            {result.requires_selection ? (
                                <div className="mt-6">
                                    <h4 className={classes.h4Text}>
                                        {t('admin.certificateNames.verify.candidates')}
                                    </h4>
                                    <p className="text-xs text-gray-500 pt-1 pb-3">
                                        {t('admin.certificateNames.verify.candidatesHint')}
                                    </p>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {t('admin.certificateNames.verify.id')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('admin.certificateNames.name')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('admin.certificateNames.category')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(result.candidates ?? []).map((candidate) => (
                                                <TableRow key={candidate.id}>
                                                    <TableCell>{candidate.id}</TableCell>
                                                    <TableCell>{candidate.name}</TableCell>
                                                    <TableCell>
                                                        {formatCertificateCategory(
                                                            t,
                                                            candidate.category,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : null}
                        </Paper>
                    ) : (
                        <Typography variant="body2" className="mt-8 text-gray-500">
                            {t('admin.certificateNames.verify.noResultYet')}
                        </Typography>
                    )}
                </div>
            }
        />
    );
};

export default VerifyNamePage;
